import { useState, type FormEvent } from "react";
import { LogOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { AuthContextValue } from "@/auth/authContext";

// Brand marks — inline so the dialog stays self-contained (no icon-font brand glyphs).
function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

type Pending = "google" | "microsoft" | "email" | null;

// §9.2 The studio's shared sign-in dialog. Signed out: the three providers (§9.2.1) plus an
// email/password form. Signed in: a compact account view with Sign out (§9.2.2) — reused by
// the §9.3 avatar later. Errors surface as toasts from AuthProvider, not here.
export function SignInDialog({
  open,
  onOpenChange,
  onSignedIn,
  auth,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Runs the instant a sign-in succeeds, before the dialog closes (§9.4.2 resume).
  onSignedIn?: () => void;
  auth: Pick<
    AuthContextValue,
    | "status"
    | "user"
    | "signInWithGoogle"
    | "signInWithMicrosoft"
    | "signInWithEmail"
    | "signOut"
  >;
}) {
  const {
    status,
    user,
    signInWithGoogle,
    signInWithMicrosoft,
    signInWithEmail,
    signOut,
  } = auth;
  const [pending, setPending] = useState<Pending>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const busy = pending !== null;

  // Close on a successful sign-in; leave the dialog open (fields intact) on failure so the
  // user can retry after reading the toast.
  const run = async (which: Pending, action: () => Promise<boolean>) => {
    setPending(which);
    const ok = await action();
    setPending(null);
    if (ok) {
      setPassword("");
      onSignedIn?.();
      onOpenChange(false);
    }
  };

  const onEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !email || !password) return;
    void run("email", () => signInWithEmail(email, password));
  };

  const signedIn = status === "in" && user;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        {signedIn ? (
          <>
            <DialogHeader>
              <DialogTitle>Your account</DialogTitle>
              <DialogDescription>
                Signed in as {signedIn.displayName || signedIn.email}.
              </DialogDescription>
            </DialogHeader>
            <Button
              variant="outline"
              className="w-full"
              disabled={busy}
              onClick={async () => {
                await signOut();
                onOpenChange(false);
              }}
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Sign in to DeckAI</DialogTitle>
              <DialogDescription>
                Use the same account across the studio, the library, and the
                add-in.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                className="w-full"
                disabled={busy}
                onClick={() => run("google", signInWithGoogle)}
              >
                <GoogleMark />
                <span className="ml-2">Continue with Google</span>
              </Button>
              <Button
                variant="outline"
                className="w-full"
                disabled={busy}
                onClick={() => run("microsoft", signInWithMicrosoft)}
              >
                <MicrosoftMark />
                <span className="ml-2">Continue with Microsoft</span>
              </Button>
            </div>

            <div className="flex items-center gap-3 py-1 text-xs text-slate-400">
              <span className="h-px flex-1 bg-slate-200" />
              or
              <span className="h-px flex-1 bg-slate-200" />
            </div>

            <form className="flex flex-col gap-3" onSubmit={onEmailSubmit}>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="signin-email">Email</Label>
                <Input
                  id="signin-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="signin-password">Password</Label>
                <Input
                  id="signin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={busy || !email || !password}
              >
                {pending === "email" ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
